import React, { useState, useEffect } from 'react';
import useAuthContext from 'contexts/AuthContext';
import { LocalStorageKeys } from './types';
import { getLocalStorage, storeLocalStorage } from './helpers';

interface UseLocalStorageOptions {
    crossDomain?: boolean;
}

function useLocalStorage<T>(key: LocalStorageKeys, initialValue: T, options: UseLocalStorageOptions = {}): [T, React.Dispatch<React.SetStateAction<T>>] {
    const { crossDomain = false } = options;
    const { user } = useAuthContext();

    const [localStorageData, setLocalStorageData] = useState<T>(() => {
        const storedValue = getLocalStorage<T>(key, crossDomain, user?.id);
        return storedValue !== null ? storedValue : initialValue;
    });

    useEffect(() => {
        if (crossDomain && user?.id) {
            const cookieValue = getLocalStorage<T>(key, crossDomain, user.id);
            if (cookieValue !== null) {
                const currentValueStr = JSON.stringify(localStorageData);
                const cookieValueStr = JSON.stringify(cookieValue);
                if (currentValueStr !== cookieValueStr) {
                    setLocalStorageData(cookieValue);
                }
            }
        }
    }, [key, crossDomain, user?.id]);

    useEffect(() => {
        storeLocalStorage(key, localStorageData, crossDomain, user?.id);
    }, [key, localStorageData, crossDomain, user?.id]);

    return [localStorageData, setLocalStorageData];
}

export function useAsyncLocalStorage<T>(key: LocalStorageKeys, defaultValue: T, options: UseLocalStorageOptions = {}) {
    const [value, setValue] = useLocalStorage<T>(key, defaultValue, options);

    const setAsync = (newValue: T) => new Promise<void>(resolve => {
        setValue(newValue);
        setTimeout(resolve, 0); // resolves on next tick
    });

    return [value, setAsync] as const;
}

export default useLocalStorage;
